import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge, Box, Button, DataItem, IconButton, TextField } from "@deere/isg.component-library";
import {
  AiGenerate,
  IconAutomation,
  IconChevronLeft,
  IconCircleClose,
  IconPerson,
  Dumptruck,
  IconJobDetailsGuidance,
} from "@deere/icons/new";
import "./OrchestratorAIPanel.css";
import { createRoot } from "react-dom/client";

import { useMap } from "@vis.gl/react-google-maps";

function OrchestratorAIPanel({
  fullMQTT,
  orgDataSession,
  currentTopicString,
  propertyType,
  selectedProperty,
  setSelectedProperty,
  handleClose,
  changeFirstPanel,
}: {
  fullMQTT: any;
  orgDataSession: any;
  currentTopicString: any;
  propertyType: String;
  selectedProperty: any;
  setSelectedProperty: (object: any) => void;
  handleClose: () => void;
  changeFirstPanel: () => void;
}) {
  const [propertyName, setPropertyName] = useState(selectedProperty?.name || "");
  const [propertyDescription, setPropertyDescription] = useState("");
  // const [propertyType, setPropertyType] = useState(selectedProperty?.type || '');
  // const [zoneOptions, setZoneOptions] = useState(['FREE_SPACE_ZONE', 'RESTRICTED_ZONE', 'BOUNDARY', 'OI_OBJECT', 'WORK_ZONE']);
  const [zoneOptions, setZoneOptions] = useState([
    "Free Space Zone",
    "Restricted Zone",
    "Boundary",
    "OI Object",
    "Work Zone",
  ]);
  // const [lineOptions, setLineOptions] = useState(['NAVIGABLE_PATH', 'WINDROW', 'STATIC_ROUTE']);
  const [lineOptions, setLineOptions] = useState(["Navigable Path", "Windrow", "Static Route"]);
  const [selectedOption, setSelectedOption] = useState("");
  const [alertVisible, setAlertVisible] = useState(true);
  const map = useMap("mainMap"); // Get the map instance
  const [points, setPoints] = useState<google.maps.LatLng[]>([]);
  const pointsRef = useRef(points); // Create a ref to hold the latest points
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [messages, setMessages] = useState<any>([
    {
      type: "orchestrator",
      content:
        "Hi, I'm Orchestrator AI! You can ask me about your Jobsite, how to use this system, or even for summaries of your Jobsite performance!",
    }, // type === 'orchestrator'
  ]); // State to hold chat messages
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("Processing..."); // New Loading Status State
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Animation Refs
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Cycle loading messages
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isLoading) {
      const steps = [
        "Analyzing Jobsite Context...",
        "Interpreting User Request...",
        "Consulting Deere AI...",
        "Generating Mission Strategy...",
        "Validating Zones and Paths...",
        "Formulating Response...",
      ];
      let i = 0;
      setLoadingStatus(steps[0]);
      interval = setInterval(() => {
        i++;
        if (i < steps.length) {
          setLoadingStatus(steps[i]);
        }
      }, 4000); // 4 seconds per step
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      stopAnimation();
    };
  }, []);

  const stopAnimation = () => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
    if (markerRef.current) {
      markerRef.current.map = null;
      markerRef.current = null;
    }
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }
  };

  const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

  interface PolylineProps {
    points: string; // A string representing the points of the polyline, e.g., "10,10 190,10 160,190 40,190"
    stroke?: string; // Optional stroke color for the line
    strokeWidth?: number; // Optional stroke width for the line
  }

  const Polyline: React.FC<PolylineProps> = ({
    points,
    stroke = "black", // Default stroke color
    strokeWidth = 2, // Default stroke width
  }) => {
    return (
      <svg width="32" height="32" viewBox="0 0 200 200">
        <polyline
          points={points}
          fill="none" // No fill for the polyline
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  };

  interface PolygonProps {
    points: string; // A string representing the points of the polygon, e.g., "50,150 100,50 150,150"
    fill?: string; // Optional fill color for the polygon
    stroke?: string; // Optional stroke color for the polygon
    strokeWidth?: number; // Optional stroke width for the polygon
  }

  const Polygon: React.FC<PolygonProps> = ({
    points,
    fill = "lightblue", // Default fill color
    stroke = "black", // Default stroke color
    strokeWidth = 1, // Default stroke width
  }) => {
    return (
      <svg width="32" height="32" viewBox="0 0 200 200">
        <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
      </svg>
    );
  };

  useEffect(() => {
    // Update the ref to the latest points
    pointsRef.current = points;
  }, [points]); // Update ref whenever points change

  useEffect(() => {
    // This is the effect to draw points on the map
    if (map) {
      let tempFeature = map.data.getFeatureById("temporaryBoundaryID");
      if (tempFeature) {
        // Found the current feature
        tempFeature.setGeometry(new google.maps.Data.LineString(points));
      } else {
        // Creates a new feature
        let myPolygon = new google.maps.Data.LineString(points);
        let myFeatureOptions: google.maps.Data.FeatureOptions = {
          geometry: myPolygon,
          id: "temporaryBoundaryID", //This is a custom id randomly generated
          properties: {
            zoneType: "TEMPORARY",
          },
        };
        let myNewFeature = new google.maps.Data.Feature(myFeatureOptions);
        map.data.add(myNewFeature);
      }
    }
  }, [points]);

  function handleZoneSelection(zoneTypeSelected: String) {
    console.log(zoneTypeSelected);
  }

  // ['Free Space Zone', 'Restricted Zone', 'Boundary', 'OI Object', 'Work Zone']

  const zoneItems = [
    {
      // Boundary
      onClick: () => handleZoneSelection("Boundary"),
      id: "2",
      title: "Boundary",
      subtitle: "An defined area",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#FFD700" // Gold
          strokeWidth={4}
          fill="#FFFACD" // Light Yellow
        />
      ),
    },
    {
      // Free Space Zone
      onClick: () => handleZoneSelection("Free Space Zone"),
      id: "0",
      title: "Free Space Zone",
      subtitle: "An easily navigatable area",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#32CD32" // Lime Green
          strokeWidth={4}
          fill="#90EE90" // Light Green
        />
      ),
    },
    {
      // Work Zone
      onClick: () => handleZoneSelection("Work Zone"),
      id: "4",
      title: "Work Zone",
      subtitle: "Typical operation area",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#FF8C00" // Dark Orange
          strokeWidth={6}
          fill="#FFE4B5" // Lemon Chiffon
        />
      ),
    },
    {
      // Restricted Zone
      onClick: () => handleZoneSelection("Restricted Zone"),
      id: "1",
      title: "Restricted Zone",
      subtitle: "An area to never traverse",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#FF4500" // Orange Red
          strokeWidth={6}
          fill="#FFCCCB" // Light Red
        />
      ),
    },
    {
      // OI Object
      onClick: () => handleZoneSelection("OI Object"),
      id: "3",
      title: "OI Object",
      subtitle: "An identified obstacle",
      Icon: (
        <Polygon
          points="50,50 150,20 200,100 150,180 50,150"
          stroke="#1E90FF" // Dodger Blue
          strokeWidth={6}
          fill="#ADD8E6" // Light Blue
        />
      ),
    },
  ];

  // ['Navigable Path', 'Windrow', 'Static Route']
  const lineItems = [
    {
      // Navigable Path
      onClick: () => handleZoneSelection("Navigable Path"),
      id: "0",
      title: "Navigable Path",
      subtitle: "A path that can be followed",
      Icon: (
        <Polyline
          points="50,150 100,100 150,130 200,80"
          stroke="#32CD32" // Lime Green
          strokeWidth={4}
        />
      ),
    },
    {
      // Windrow
      onClick: () => handleZoneSelection("Windrow"),
      id: "1",
      title: "Windrow",
      subtitle: "A pass taken by a grading machine",
      Icon: (
        <Polyline
          points="50,150 100,100 150,130 200,80"
          stroke="#FF4500" // Orange Red
          strokeWidth={4}
        />
      ),
    },
    {
      // Static Route
      onClick: () => handleZoneSelection("Static Route"),
      id: "2",
      title: "Static Route",
      subtitle: "A route that will not change",
      Icon: (
        <Polyline
          points="50,150 100,100 150,130 200,80"
          stroke="#FFD700" // Gold
          strokeWidth={4}
        />
      ),
    },
  ];

  // Listen for clicks on the map to add points
  const clickListener = map?.addListener("click", (mouseEvent: { latLng: any }) => {
    const latLng = mouseEvent.latLng;
    const newPoint = new google.maps.LatLng(latLng.lat(), latLng.lng());
    setPoints((prevPoints) => [...prevPoints, newPoint]); // Update the points list
    console.log(`Boundary point added at Lat: ${latLng.lat()}, Lng: ${latLng.lng()}`);
  });

  async function handleSave() {
    console.log("I am saving");
    setIsSaving(true);

    if (points) {
      async function savePointsZone(pointsArray: any) {
        // const url = 'http://localhost/API/Save/Boundary'; // Replace with your actual endpoint
        const url = "https://" + backendURI + "/Save/Boundary";

        try {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json", // Specify that the content type is JSON
            },
            body: JSON.stringify({
              pointsArray: pointsArray,
              currentTopicString:
                currentTopicString + "Shape/" + Math.floor(Math.random() * 99999999).toString(),
            }), // Convert the pointsArray to JSON
          });

          // Check if the response is successful
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json(); // Parse the JSON response
          console.log("Points saved successfully:", data);
        } catch (error) {
          console.error("Error saving points:", error);
        }
      }

      async function savePointsLine(pointsArray: any) {
        // const url = 'http://localhost/API/Save/Line'; // Replace with your actual endpoint
        const url = "https://" + backendURI + "/Save/Line";

        try {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json", // Specify that the content type is JSON
            },
            body: JSON.stringify({
              pointsArray: pointsArray,
              currentTopicString:
                currentTopicString + "Line/" + Math.floor(Math.random() * 99999999).toString(),
            }), // Convert the pointsArray to JSON
          });

          // Check if the response is successful
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json(); // Parse the JSON response
          console.log("Points saved successfully:", data);
        } catch (error) {
          console.error("Error saving points:", error);
        }
      }

      const latestPoints = pointsRef.current; // Access latest points from ref
      console.log(latestPoints);
      if (latestPoints.length > 0) {
        // createNewBoundary(points); // Call the function to create a new boundary with the points
        if (propertyType === "Zone") {
          await savePointsZone(latestPoints);
        } else if (propertyType === "Line") {
          await savePointsLine(latestPoints);
        } else {
          console.log("ERROR: Improper Property Type");
        }
      } else {
        console.log("No points to save.");
      }

      console.log("Boundary creation canceled!");
      // Remove buttons from the map
      // google.maps.event.removeListener(clickListener);
      setIsSaving(false);
      handleLocalClose();
    }
  }

  function handleClear() {
    setPoints([]); // Clear the points array
  }

  async function handleLocalClose() {
    console.log("Boundary creation canceled!");
    await clickListener?.remove();
    if (clickListener) {
      await clickListener.remove();
      await google.maps.event.removeListener(clickListener);
    }
    setPoints([]); // Clear the points

    handleClose();
  }

  function closeAlert() {
    setAlertVisible(false);
  }

  // return (
  //     <div className='PropertiesPanel'>
  //         <div className='TopPanel'>
  //             <div className='TopTitleBar'>
  //                 <div className='TitleBarLeft'>
  //                     <IconButton
  //                         icon={<IconChevronLeft />}
  //                         variant='tertiary'
  //                         onClick={handleLocalClose}
  //                         style={{ width: "32px", height: "32px" }}
  //                     />
  //                     <div className='PropertyTag'>
  //                         <div className='PropertyText'>
  //                             <b>Orchestrator AI </b>
  //                             <Badge variant="warning"> Beta 0.0.1 </Badge>
  //                         </div>
  //                     </div>
  //                 </div>
  //                 <div className='exitButtons'>
  //                     <IconButton
  //                         icon={
  //                             <IconCircleClose icon={{ style: { width: "18px", height: "18px" } }} />
  //                         } variant='tertiary' onClick={handleLocalClose} />
  //                 </div>
  //             </div>
  //         </div>

  //         <div className='AIChatConversationBox'>
  //             <Box sx={{
  //                 width: "360px",
  //                 height: "auto",
  //                 marginBottom: "16px",
  //                 paddingTop: "4px",
  //                 borderRadius: 1,
  //                 bgcolor: '#f7f7f7',
  //                 // '&:hover': {
  //                 //     bgcolor: '#f0f0f0',
  //                 // },
  //             }}>
  //                 <DataItem
  //                     Icon={
  //                         <IconAutomation icon={{ style: { width: "18px", height: "18px" } }} />
  //                     }
  //                     // caption="Version: 0.0.1"
  //                     title="Orchestrator"
  //                     disabled={true}
  //                 />
  //                 <div className='chatTextContainer'>Stuff in the box</div>
  //             </Box>

  //             <Box sx={{
  //                 width: "330px",
  //                 position: "relative",
  //                 left: "56px",
  //                 height: "auto",
  //                 paddingTop: "4px",
  //                 marginBottom: "16px",
  //                 borderRadius: 1,
  //                 bgcolor: '#f0f0f0',
  //                 // '&:hover': {
  //                 //     bgcolor: '#e7e7e7',
  //                 // },
  //             }}>
  //                 <DataItem
  //                     Icon={
  //                         <IconPerson icon={{ style: { width: "18px", height: "18px" } }} />
  //                     }
  //                     title="Me"
  //                     disabled={true}
  //                     sx={{
  //                         width: "90px",
  //                         left: "251px"
  //                     }}
  //                 />
  //                 <div className='chatTextContainerUser'>Stuff in the box</div>

  //             </Box>
  //         </div>

  //         <div className='chatTextBottom'>
  //             <TextField
  //                 onChange={(event) => setPropertyDescription(event.target.value)}
  //                 placeholder="Type your instructions"
  //                 value={propertyDescription}
  //                 style={{ width: "100%", marginBottom: "10px" }}
  //             />

  //             <Button
  //                 style={{ marginLeft: "267px", marginRight: "6px", color: "black", border: "none" }}
  //                 // variant="primary"
  //                 className='SaveMissionButton'
  //                 onClick={handleClear}
  //             >
  //                 Clear
  //             </Button>
  //             <Button
  //                 loading={isSaving}
  //                 style={{ marginLeft: "6px", color: "black", border: "none" }}
  //                 variant="primary"
  //                 className='SaveMissionButton'
  //                 onClick={handleSave}
  //             >
  //                 Submit
  //             </Button>
  //         </div>

  //     </div>
  // );

  function getContext(obj: any, indent = 0) {
    // Check if the input is an object or an array
    if (obj === null) {
      return "null"; // Handle null explicitly
    }

    if (typeof obj !== "object") {
      return String(obj); // Convert non-object types to string
    }

    // Initialize the result string with appropriate indentation
    let result = "";
    const padding = " ".repeat(indent); // Create indentation spaces

    if (Array.isArray(obj)) {
      result += "[\n"; // Start array representation
      for (let i = 0; i < obj.length; i++) {
        result += `${padding}  ${getContext(obj[i], indent + 2)}`; // Recursive call for each element
        if (i < obj.length - 1) {
          result += ",\n"; // Add comma if not last element
        }
      }
      result += `\n${padding}]`; // Close array representation
    } else {
      result += "{\n"; // Start object representation
      const keys = Object.keys(obj);
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        result += `${padding}  ${String(key)}: ${getContext(obj[key], indent + 2)}`; // Recursive call for each property
        if (i < keys.length - 1) {
          result += ",\n"; // Add comma if not last property
        }
      }
      result += `\n${padding}}`; // Close object representation
    }

    return result; // Return the final string representation
  }

  // const handleSend = async (input: string) => {
  //     let thisResponse = '';
  //     const res = await fetch(`/API/AI/stream`, {
  //         method: 'POST',
  //         headers: {
  //             'Content-Type': 'application/json',
  //         },
  //         body: JSON.stringify({ messages: [input] }),
  //     });

  //     if (!res.body) {
  //         return '';
  //     }

  //     const reader = res.body.getReader();
  //     const decoder = new TextDecoder('utf-8');
  //     let done = false;

  //     while (!done) {
  //         const { done: readerDone, value } = await reader?.read();
  //         done = readerDone;
  //         if (value) {

  //             // setResponse(prev => prev + decoder.decode(value)); // Append new parts to response
  //             // addOrchMessage((prev: any) => prev + decoder.decode(value))
  //             thisResponse = thisResponse + decoder.decode(value);
  //         }
  //     }
  //     addOrchMessage(thisResponse);
  // };

  const buildAIContext = () => {
    let context: any = {
      jobsite: "Context Unavailable",
    };

    try {
      if (orgDataSession) {
        const entities: any[] = [];

        const processEntity = (key: string, val: any, typeCategory: string) => {
          if (val && typeof val === "object" && !val.deleted) {
            // Extract location summary (first and last point)
            let locationSummary = "";
            if (val.points && Array.isArray(val.points) && val.points.length > 0) {
              const first = val.points[0];
              const last = val.points[val.points.length - 1];

              // Helper to extract lat/lng from various point structures
              const getCoord = (p: any) => {
                if (!p) return null;
                // Handle { location: { latitude, longitude } } structure seen in Line/Shape
                if (p.location) {
                  return {
                    lat: p.location.latitude ?? p.location.lat,
                    lng: p.location.longitude ?? p.location.lng,
                  };
                }
                // Handle flat structure { latitude, longitude } or { lat, lng }
                return {
                  lat: p.latitude ?? p.lat,
                  lng: p.longitude ?? p.lng,
                };
              };

              const fC = getCoord(first);
              const lC = getCoord(last);

              const fmt = (c: any) =>
                c && c.lat !== undefined && c.lng !== undefined
                  ? `(${Number(c.lat).toFixed(6)}, ${Number(c.lng).toFixed(6)})`
                  : "";

              if (fC) locationSummary = `Start:${fmt(fC)}`;
              if (lC) locationSummary += ` End:${fmt(lC)}`;
            }

            entities.push({
              id: val.uuid || key, // UUID is critical for mission linking
              name: key, // The key is often the human readable name here
              type: val.zoneType || typeCategory,
              location: locationSummary,
            });
          }
        };

        // Explicitly valid categories based on observed orgDataSession structure
        if (orgDataSession.Line) {
          Object.keys(orgDataSession.Line).forEach((k) =>
            processEntity(k, orgDataSession.Line[k], "Path"),
          );
        }
        if (orgDataSession.Shape) {
          Object.keys(orgDataSession.Shape).forEach((k) =>
            processEntity(k, orgDataSession.Shape[k], "Zone"),
          );
        }

        // Fallback: If specific buckets aren't found, try a shallow search (depth 1) for anything looking like an entity
        if (entities.length === 0) {
          Object.keys(orgDataSession).forEach((topKey) => {
            const topVal = orgDataSession[topKey];
            if (topVal && typeof topVal === "object") {
              Object.keys(topVal).forEach((subKey) => {
                const subVal = topVal[subKey];
                if (subVal && (subVal.uuid || subVal.points || subVal.zoneType)) {
                  processEntity(subKey, subVal, "Object");
                }
              });
            }
          });
        }

        context = {
          summary: "User requested mission creation.",
          availableData:
            entities.length > 0 ? entities : "No named zones found. Use arbitrary coordinates.",
        };
      }
    } catch (e) {
      console.warn("Could not build context", e);
    }
    return context;
  };

  const handleSend = async (input: string) => {
    setIsLoading(true); // Start loading animation
    let thisResponse = "";

    // Prepare Context from orgDataSession
    const context = buildAIContext();

    const res = await fetch(`/API/AI/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        messages: messages
          .map((m: any) => ({
            role: m.type === "orchestrator" ? "assistant" : "user",
            content: typeof m.content === "string" ? m.content : JSON.stringify(m.content),
          }))
          .concat([{ role: "user", content: input }]),
        context: context,
      }),
    });

    if (!res.body) {
      setIsLoading(false); // Stop loading animation
      return "";
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let done = false;

    while (!done) {
      const { done: readerDone, value } = await reader?.read();
      done = readerDone;
      if (value) {
        thisResponse = thisResponse + decoder.decode(value);
      }
    }

    try {
      const parsed = JSON.parse(thisResponse);

      // Check for AI Gateway style outputs wrapper or direct OpenAI style
      let contentResult = parsed;

      // Case 1: Legacy AI Gateway "outputs" array
      if (parsed.outputs && parsed.outputs.length > 0) {
        try {
          contentResult = JSON.parse(parsed.outputs[0].content);
        } catch (e) {
          contentResult = parsed.outputs[0].content;
        }
      }
      // Case 2: OpenAI-compatible format (directResult from backend)
      else if (parsed.choices && parsed.choices.length > 0 && parsed.choices[0].message) {
        const rawContent = parsed.choices[0].message.content;
        try {
          contentResult = JSON.parse(rawContent);
        } catch (e) {
          contentResult = rawContent;
        }
      }
      // Case 3: Wrapped in "directResult" property (if backend didn't flatten it)
      else if (parsed.directResult && parsed.directResult.choices) {
        const rawContent = parsed.directResult.choices[0].message.content;
        try {
          contentResult = JSON.parse(rawContent);
        } catch (e) {
          contentResult = rawContent;
        }
      }

      if (
        typeof contentResult === "object" &&
        contentResult !== null &&
        contentResult.intent === "create_mission"
      ) {
        // AI wants to create a mission
        addOrchMessage(contentResult);
      } else if (parsed.error) {
        addOrchMessage(`Error: ${parsed.error} - ${parsed.message || ""}`);
      } else if (typeof contentResult === "string") {
        addOrchMessage(contentResult);
      } else {
        // Fallback: If we couldn't find a standard content field, warn but show what we have
        // Only show if it's not the raw big JSON
        if (contentResult === parsed) {
          // If we are here, it means we failed to extract content from specific fields
          // Try to see if it's the raw OpenAI object and we just missed the content
          if (parsed.choices && parsed.choices[0].finish_reason === "length") {
            addOrchMessage(
              "Error: AI response was truncated (token limit reached). Please try again or ask for a shorter response.",
            );
          } else {
            addOrchMessage(JSON.stringify(contentResult, null, 2));
          }
        } else {
          addOrchMessage(JSON.stringify(contentResult, null, 2));
        }
      }
    } catch (e) {
      // Fallback for plain text or partial partials
      addOrchMessage(thisResponse);
    }

    setIsLoading(false); // Stop loading animation
  };

  // Function to add an Orchestrator message
  const addOrchMessage = (message: any) => {
    setMessages((prevMessages: any) => [
      ...prevMessages,
      { type: "orchestrator", content: message },
    ]);
  };

  // Function to add a User message
  const addUserMessage = (message: any) => {
    setMessages((prevMessages: any) => [...prevMessages, { type: "user", content: message }]);
  };

  const handleSendMessageUser = () => {
    if (propertyDescription.trim() !== "") {
      addUserMessage(propertyDescription);
      handleSend(propertyDescription);
      setPropertyDescription("");
    }
  };

  // --- Mission Handling ---
  const findLocationByUUID = (obj: any, targetUUID: string): any => {
    if (!obj || typeof obj !== "object") return null;

    // Check if this object is the target
    // Assuming objects might be keyed by UUID or have an id/uuid field
    if (obj.uuid === targetUUID || obj.id === targetUUID) {
      // Check for location/pose/geometry
      if (obj.location) return obj.location;
      if (obj.volume?.center) return obj.volume.center;
      if (obj.points && obj.points.length > 0) return obj.points; // Return all points for centroid calc
      if (obj.type === "Feature" && obj.geometry) return obj.geometry; // GeoJSON Feature
    }

    // Recurse keys
    for (const key of Object.keys(obj)) {
      // If the KEY is the UUID (common in this schema)
      if (key === targetUUID) {
        const child = obj[key];
        if (child.location) return child.location;
        if (child.geometry) return child.geometry;
        if (child.points) return child.points;
      }

      const found = findLocationByUUID(obj[key], targetUUID);
      if (found) return found;
    }
    return null;
  };

  const handlePreviewMission = (mission: any) => {
    if (mission && mission.tasks) {
      const taskPoints: google.maps.LatLng[] = [];
      const actionPoints: { index: number; task: any }[] = [];

      mission.tasks.forEach((task: any, idx: number) => {
        // Case 1: Direct Coordinates in AI response
        let lat, lng;
        if (task.properties && task.properties.location) {
          lat = task.properties.location.lat;
          lng = task.properties.location.lng;
        }
        // Case 2: UUID Reference (Need to look up in orgDataSession)
        else if (task.properties && task.properties.uuid && orgDataSession) {
          // Try to find the object in JobSite -> Session -> Objects/Zones
          const foundLoc = findLocationByUUID(orgDataSession, task.properties.uuid);
          if (foundLoc) {
            // 0. Array of Points (Zone/Line) - Calculate Centroid
            if (Array.isArray(foundLoc)) {
              let sumLat = 0,
                sumLng = 0,
                count = 0;
              foundLoc.forEach((p: any) => {
                const pLat = p.lat ?? p.latitude ?? p.location?.lat ?? p.location?.latitude;
                const pLng = p.lng ?? p.longitude ?? p.location?.lng ?? p.location?.longitude;
                if (pLat !== undefined && pLng !== undefined) {
                  sumLat += Number(pLat);
                  sumLng += Number(pLng);
                  count++;
                }
              });
              if (count > 0) {
                lat = sumLat / count;
                lng = sumLng / count;
              }
            }
            // 1. Standard { lat, lng } or { latitude, longitude }
            else if (foundLoc.lat || foundLoc.latitude) {
              lat = foundLoc.lat || foundLoc.latitude;
              lng = foundLoc.lng || foundLoc.longitude;
            }
            // 2. Nested location object (common in Line/Shape structures)
            else if (
              foundLoc.location &&
              (foundLoc.location.lat !== undefined || foundLoc.location.latitude !== undefined)
            ) {
              lat = foundLoc.location.lat ?? foundLoc.location.latitude;
              lng = foundLoc.location.lng ?? foundLoc.location.longitude;
            }
            // 3. GeoJSON Point Geometry
            else if (foundLoc.type === "Point" && Array.isArray(foundLoc.coordinates)) {
              lat = foundLoc.coordinates[1];
              lng = foundLoc.coordinates[0];
            }
            // 4. GeoJSON Polygon Geometry
            else if (
              foundLoc.type === "Polygon" &&
              Array.isArray(foundLoc.coordinates) &&
              foundLoc.coordinates[0]
            ) {
              // Simple centroid of outer ring
              let sumLat = 0,
                sumLng = 0,
                count = 0;
              foundLoc.coordinates[0].forEach((c: any) => {
                sumLng += c[0];
                sumLat += c[1];
                count++;
              });
              if (count > 0) {
                lat = sumLat / count;
                lng = sumLng / count;
              }
            }
          }
        }

        if (lat !== undefined && lng !== undefined) {
          taskPoints.push(new google.maps.LatLng(lat, lng));
          // Mark this point index as having an action
          actionPoints.push({ index: taskPoints.length - 1, task: task });
        }
      });

      if (taskPoints.length > 0) {
        setPoints(taskPoints); // This triggers the map draw effect (polyline)

        // Pan map
        if (map) {
          const bounds = new google.maps.LatLngBounds();
          taskPoints.forEach((p) => bounds.extend(p));
          map.fitBounds(bounds);
          if (taskPoints.length === 1) {
            map.setZoom(18);
            map.setCenter(taskPoints[0]);
          }
        }

        // Start Animation
        animateMission(taskPoints, actionPoints);
      } else {
        console.warn("No valid points found to preview.");
      }
    }
  };

  const animateMission = async (
    routePoints: google.maps.LatLng[],
    actionPoints: { index: number; task: any }[],
  ) => {
    if (!map || routePoints.length < 1) return;

    stopAnimation(); // Clear existing

    // 1. Create Marker
    // The marker library must be requested before using AdvancedMarkerElement
    const { AdvancedMarkerElement } = (await google.maps.importLibrary(
      "marker",
    )) as google.maps.MarkerLibrary;

    const markerDiv = document.createElement("div");
    const root = createRoot(markerDiv);
    const icon = <Dumptruck style={{ minHeight: "48px", minWidth: "48px" }} outline={"#367c2b"} />; // John Deere Green
    root.render(<div>{icon}</div>);

    markerRef.current = new AdvancedMarkerElement({
      position: routePoints[0],
      content: markerDiv,
      map,
      zIndex: 999999,
      title: "Preview Machine",
    });

    markerRef.current = new AdvancedMarkerElement({
      position: routePoints[0],
      content: markerDiv,
      map,
      zIndex: 999999,
      title: "Preview Machine",
    });

    // 2. Interpolate Path
    // We map original indices to interpolated indices to know when to show actions
    const interpolatedPoints: google.maps.LatLng[] = [];
    const actionIndices = new Map<number, any>(); // Map interpolated index -> task action

    // If single point, just hover there
    if (routePoints.length === 1) {
      interpolatedPoints.push(routePoints[0]);
      actionIndices.set(0, actionPoints[0]?.task);
    } else {
      for (let i = 0; i < routePoints.length - 1; i++) {
        const start = routePoints[i];
        const end = routePoints[i + 1];

        // Register action at the start of this segment if applicable
        const actionAtStart = actionPoints.find((ap) => ap.index === i);
        if (actionAtStart) {
          actionIndices.set(interpolatedPoints.length, actionAtStart.task);
        }

        const latDiff = end.lat() - start.lat();
        const lngDiff = end.lng() - start.lng();
        const distance = Math.sqrt(latDiff ** 2 + lngDiff ** 2);

        // Adjust step size for animation speed (smaller = smoother/slower)
        const stepSize = 0.00005;
        const steps = Math.max(1, Math.floor(distance / stepSize));

        for (let j = 0; j < steps; j++) {
          const t = j / steps;
          interpolatedPoints.push(
            new google.maps.LatLng(start.lat() + t * latDiff, start.lng() + t * lngDiff),
          );
        }
      }
      // Add final point
      const lastIdx = routePoints.length - 1;
      interpolatedPoints.push(routePoints[lastIdx]);

      const actionAtEnd = actionPoints.find((ap) => ap.index === lastIdx);
      if (actionAtEnd) {
        actionIndices.set(interpolatedPoints.length - 1, actionAtEnd.task);
      }
    }

    // 3. Animate Loop
    let index = 0;
    let pauseCounter = 0; // Frames to pause for action

    animationIntervalRef.current = setInterval(() => {
      if (!markerRef.current) return;

      // Handle Action Pause
      if (pauseCounter > 0) {
        pauseCounter--;
        if (pauseCounter === 0 && infoWindowRef.current) {
          infoWindowRef.current.close();
          infoWindowRef.current = null;
        }
        return;
      }

      if (index < interpolatedPoints.length) {
        // Update position
        markerRef.current.position = interpolatedPoints[index];

        // Check for action at this index
        if (actionIndices.has(index)) {
          const task = actionIndices.get(index);
          const actionName = task.action;

          // Show Info Window
          if (infoWindowRef.current) infoWindowRef.current.close();

          const contentString = `
                        <div style="padding: 5px; color: black;">
                           <b>${actionName}</b><br/>
                           ${task.properties?.duration ? `Wait: ${task.properties.duration}s` : ""}
                        </div>
                     `;

          infoWindowRef.current = new google.maps.InfoWindow({
            content: contentString,
            pixelOffset: new google.maps.Size(0, -20),
          });
          infoWindowRef.current.open(map, markerRef.current);

          // Pause animation to visualize action (e.g., 60 frames = ~1 sec at 16ms interval)
          pauseCounter = 60;
        }

        index++;
      } else {
        // Loop or Stop? Let's just stop for now, or loop.
        // Stop:
        // stopAnimation();

        // Loop:
        index = 0;
      }
    }, 16); // ~60fps
  };

  const handleSaveMission = async (missionWrapper: any) => {
    const url = `https://${backendURI}/Save/Mission`;
    const mission = missionWrapper.mission;

    const missionId =
      mission.missionName.replace(/\s+/g, "_") + "_" + Math.floor(Math.random() * 1000);

    const payload = {
      missionObject: {
        missionId: missionId,
        missionName: mission.missionName,
        missionType: "Delivery", // Default or derived
        missionDescription: mission.missionDescription,
        taskRepetitions: mission.taskRepetitions || "1",
        vehicleID: "All", // Default or derived
        tasks: mission.tasks,
      },
      currentTopicString: `${currentTopicString}Missions/${missionId}`,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      // Notify user
      addOrchMessage(`Mission "${mission.missionName}" saved successfully!`);
    } catch (error) {
      console.error("Error saving mission:", error);
      addOrchMessage(`Error saving mission: ${error}`);
    }
  };

  const LoadingDots = () => {
    return (
      <div className="messageRow orchestratorRow">
        <div className="avatar orchestratorAvatar">
          <AiGenerate size={20} />
        </div>
        <div className="messageBubble orchestratorBubble loadingBubble">
          <div
            style={{ marginRight: "10px", fontSize: "12px", color: "#555", fontStyle: "italic" }}
          >
            {loadingStatus}
          </div>
          <div className="loading-dots">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="PropertiesPanelOAI">
      <div className="TopPanel">
        <div className="TopTitleBar">
          <div className="TitleBarLeft">
            <IconButton
              icon={
                <span>
                  <IconChevronLeft size={24} />
                </span>
              }
              variant="borderless"
              onClick={handleLocalClose}
              style={{ width: "32px", height: "32px" }}
            />
            <div className="PropertyTag">
              <div className="PropertyText">
                <b>Orchestrator AI </b>
                <Badge variant="warning"> Beta 0.0.2 </Badge>
              </div>
            </div>
          </div>
          <div className="exitButtons">
            <IconButton
              icon={
                <span>
                  <IconCircleClose size={18} />
                </span>
              }
              variant="borderless"
              onClick={handleLocalClose}
            />
          </div>
        </div>
      </div>

      <div className="AIChatConversationBox">
        {messages.map((msg: any, index: any) => (
          <div
            key={index}
            className={`messageRow ${msg.type === "orchestrator" ? "orchestratorRow" : "userRow"}`}
          >
            {msg.type === "orchestrator" && (
              <div className="avatar orchestratorAvatar">
                <AiGenerate size={20} />
              </div>
            )}
            <div
              className={`messageBubble ${msg.type === "orchestrator" ? "orchestratorBubble" : "userBubble"}`}
            >
              {msg.type === "orchestrator" ? (
                <div className="chatTextContainer">
                  {typeof msg.content === "object" && msg.content.intent === "create_mission" ? (
                    <div className="mission-proposal-card">
                      <h4>Mission Proposed: {msg.content.mission.missionName}</h4>
                      <p>{msg.content.explanation}</p>
                      <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                        <Button onClick={() => handlePreviewMission(msg.content.mission)}>
                          Preview
                        </Button>
                        <Button onClick={() => handleSaveMission(msg.content)}>Save</Button>
                      </div>
                      <details style={{ marginTop: "8px" }}>
                        <summary>JSON Details</summary>
                        <pre style={{ fontSize: "10px", overflow: "auto" }}>
                          {JSON.stringify(msg.content.mission, null, 2)}
                        </pre>
                      </details>
                    </div>
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {typeof msg.content === "string" ? msg.content : JSON.stringify(msg.content)}
                    </ReactMarkdown>
                  )}
                </div>
              ) : (
                <div className="chatTextContainerUser">{msg.content}</div>
              )}
            </div>
          </div>
        ))}
        {isLoading && <LoadingDots />}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ padding: "4px 12px", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
        <button
          onClick={() => {
            const ctx = buildAIContext();
            console.log("--- AI CONTEXT ---");
            console.log(ctx);
            addOrchMessage("Logged Context to Browser Console (F12)");
          }}
          style={{
            fontSize: "10px",
            cursor: "pointer",
            background: "none",
            border: "1px solid #666",
            color: "#ccc",
            borderRadius: "4px",
          }}
        >
          Log Context
        </button>
        <button
          onClick={() => {
            console.log("--- FULL ORG DATA ---");
            console.log(orgDataSession);
            addOrchMessage("Logged OrgData to Browser Console (F12)");
          }}
          style={{
            fontSize: "10px",
            cursor: "pointer",
            background: "none",
            border: "1px solid #666",
            color: "#ccc",
            borderRadius: "4px",
          }}
        >
          Log OrgData
        </button>
      </div>

      <div className="chatInputArea">
        <div className="inputWrapper">
          <TextField
            onChange={(event) => setPropertyDescription(event.target.value)}
            placeholder="Type a message..."
            value={propertyDescription}
            multiline
            maxRows={5}
            style={{ width: "100%" }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessageUser();
              }
            }}
          />
        </div>
        <div className="sendButtonContainer">
          <Button
            variant="primary"
            onClick={handleSendMessageUser}
            disabled={isLoading || propertyDescription.trim() === ""}
            style={{ minWidth: "40px", padding: "10px" }}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

export default OrchestratorAIPanel;
